import { Module } from '../Module';
import { ChangeUserPasswordRequest } from '../ServerApi/requests/ChangeUserPasswordRequest';
import { GeneratePublicKeyRequest, IGeneratePublicKeyResult } from '../ServerApi/requests/GeneratePublicKeyRequest';
import {
  GetMarkAnimationSettingRequest,
   IGetMarkAnimationSettingParams
} from '../ServerApi/requests/GetMarkAnimationSettingRequest';
import {
  GetSuspendedDataAlertSettingRequest,
  IGetSuspendedDataAlertSettingParams
} from '../ServerApi/requests/GetSuspendedDataAlertSettingRequest';
import {
  GetSuspendedSubscriptionSettingRequest,
  IGetSuspendedSubscriptionSettingParams
} from '../ServerApi/requests/GetSuspendedSubscriptionSettingRequest';
import {
  IUpdateMarkAnimationSettingParams,
  UpdateMarkAnimationSettingRequest,
} from '../ServerApi/requests/UpdateMarkAnimationSettingRequest';
import {
  IUpdateNotificationSettingParams,
  UpdateNotificationSettingRequest,
} from '../ServerApi/requests/UpdateNotificationSettingRequest';
import { IUpdateSiteStartPageParams, UpdateSiteStartPageRequest } from '../ServerApi/requests/UpdateSiteStartPageRequest';
import {
  IUpdateSuspendedDataAlertSettingParams,
  UpdateSuspendedDataAlertSettingRequest,
} from '../ServerApi/requests/UpdateSuspendedDataAlertSettingRequest';
import {
  IUpdateSuspendedSubscriptionSettingParams,
  UpdateSuspendedSubscriptionSettingRequest,
} from '../ServerApi/requests/UpdateSuspendedSubscriptionSettingRequest';

import { SetShowMetricsBannerRequest } from '../ServerApi/requests/SetShowMetricsBannerRequest';
import { SetShowWelcomeScreenRequest } from '../ServerApi/requests/SetShowWelcomeScreenRequest';
import {
  IUpdateUserDisplayNameParams,
  UpdateUserDisplayNameRequest,
} from '../ServerApi/requests/UpdateUserDisplayNameRequest';
import { IUpdateUserEmailParams, UpdateUserEmailRequest } from '../ServerApi/requests/UpdateUserEmailRequest';
import { IUpdateUserLanguageParams, UpdateUserLanguageRequest } from '../ServerApi/requests/UpdateUserLanguageRequest';
import { IUpdateUserLocaleParams, UpdateUserLocaleRequest } from '../ServerApi/requests/UpdateUserLocaleRequest';
import {
  IUpdateUserLocalTimeZoneDataParams,
  UpdateUserLocalTimeZoneDataRequest,
} from '../ServerApi/requests/UpdateUserLocalTimeZoneDataRequest';
import { IUpdateUserStartPageParams, UpdateUserStartPageRequest } from '../ServerApi/requests/UpdateUserStartPageRequest';
import { IResult } from '../ServerApi/types/IResponse';
import { ErrorNotifyingServer } from '../services/ErrorNotifyingServer';
import { ServerService } from '../services/ServerService';


export class UserSettings {

  public static $inject = ['ErrorNotifyingServer'];
  // Const
  private static ResetStartPage = 'unspecified';

  // Note: This is used as sentinel value. It should not be mutated.
  public static SameUserId: string = null;

  private server: ServerService;

  constructor(private ErrorNotifyingServer: ErrorNotifyingServer) {
    this.server = ErrorNotifyingServer.getServerService();
  }

  public updateUserDisplayName(userId: string, displayName: string): ng.IPromise<any> {
    const params: IUpdateUserDisplayNameParams = {
      userId: userId,
      displayName: displayName
    };
    return this.ErrorNotifyingServer.sendRequestAndDelay(new UpdateUserDisplayNameRequest(params));
  }

  public updateUserEmail(userId: string, email: string): ng.IPromise<any> {
    const params: IUpdateUserEmailParams = {
      userId: userId,
      email: email
    };
    return this.ErrorNotifyingServer.sendRequest(new UpdateUserEmailRequest(params));
  }

  public updateUserEmailAndDelay(userId: string, email: string): ng.IPromise<any> {
      const params: IUpdateUserEmailParams = {
          userId: userId,
          email: email
      };
      return this.ErrorNotifyingServer.sendRequestAndDelay(new UpdateUserEmailRequest(params));
  }

  public updateUserLanguage(userId: string, language: string): ng.IPromise<any> {
    const params: IUpdateUserLanguageParams = {
      userId: userId,
      language: language
    };
    return this.ErrorNotifyingServer.sendRequest(new UpdateUserLanguageRequest(params));
  }

  public updateUserLocale(userId: string, locale: string): ng.IPromise<any> {
    const params: IUpdateUserLocaleParams = {
      userId: userId,
      locale: locale
    };
    return this.ErrorNotifyingServer.sendRequest(new UpdateUserLocaleRequest(params));
  }

  public updateUserLocalTimeZoneData(offset: number, timeZoneId: string, isUserSetTimeZone: boolean): ng.IPromise<any> {
    const params: IUpdateUserLocalTimeZoneDataParams = {
      offset: offset,
      timeZoneId: timeZoneId,
      isUserSetTimeZone: isUserSetTimeZone
    };
    return this.ErrorNotifyingServer.sendRequest(new UpdateUserLocalTimeZoneDataRequest(params));
  }

  public updateUserStartPage(userId: string, startPage: string): ng.IPromise<any> {
    const params: IUpdateUserStartPageParams = {
      startPage: startPage
    };

    if (userId !== UserSettings.SameUserId) {
      params.userId = userId;
    }
    return this.ErrorNotifyingServer.sendRequest(new UpdateUserStartPageRequest(params));
  }

  public updateCurrentUsersStartPage(startPage: string): ng.IPromise<any> {
    const params: IUpdateUserStartPageParams = {
      startPage: startPage
    };
    return this.ErrorNotifyingServer.sendRequest(new UpdateUserStartPageRequest(params));
  }

  public updateSiteStartPage(startPage: string): ng.IPromise<any> {
    const params: IUpdateSiteStartPageParams = {
      startPage: startPage
    };
    return this.ErrorNotifyingServer.sendRequest(new UpdateSiteStartPageRequest(params));
  }

  public updateWelcomeChannelEnabled(showWelcomeScreen: boolean): ng.IPromise<any> {
    return this.ErrorNotifyingServer.sendRequest(new SetShowWelcomeScreenRequest({ showWelcomeScreen }));
  }

  public updateShowMetricsBanner(showMetricsBanner: boolean): ng.IPromise<any> {
    return this.ErrorNotifyingServer.sendRequest(new SetShowMetricsBannerRequest({ showMetricsBanner }));
  }

  public resetUserStartPage(userId: string): ng.IPromise<any> {
    return this.updateUserStartPage(userId, UserSettings.ResetStartPage);
  }

  public changeUserPassword(
    userId: string,
    newPassword: string,
    oldPassword?: string): ng.IPromise<IResult> {

    return this.server.sendRequest(new GeneratePublicKeyRequest()).
      then((generatePublicKeyResult: IGeneratePublicKeyResult) => {
        const key = generatePublicKeyResult.key;
        const keyId = generatePublicKeyResult.keyId;

        // server admin does not need to enter old password to change a user's password
        let oldEncryptedPassword: string;
        if (oldPassword && oldPassword.length > 0) {
          oldEncryptedPassword = rsa.encrypt(oldPassword, key);
        }

        const newEncryptedPassword = rsa.encrypt(newPassword, key);

        const changePasswordRequest = new ChangeUserPasswordRequest({
          userId: userId,
          oldEncryptedPassword: oldEncryptedPassword,
          newEncryptedPassword: newEncryptedPassword,
          keyId: keyId
        });
        return this.ErrorNotifyingServer.sendRequest(changePasswordRequest);
      });
  }

  public updateNotificationSetting(userId: string, enabled: boolean): ng.IPromise<any> {
    const params: IUpdateNotificationSettingParams = {
      userId: userId,
      enabled: enabled
    };
    return this.ErrorNotifyingServer.sendRequest(new UpdateNotificationSettingRequest(params));
  }

  public updateSuspendedSubscriptionSetting(user: string, enabled: boolean): ng.IPromise<any> {
    const params: IUpdateSuspendedSubscriptionSettingParams = {
      user,
      enabled
    };
    return this.ErrorNotifyingServer.sendRequest(new UpdateSuspendedSubscriptionSettingRequest(params));
  }

  public getSuspendedSubscriptionSetting(user: string): ng.IPromise<any> {
    const params: IGetSuspendedSubscriptionSettingParams = {
      user
    };
    return this.ErrorNotifyingServer.sendRequest(new GetSuspendedSubscriptionSettingRequest(params));
  }

  public updateSuspendedDataAlertSetting(user: string, enabled: boolean): ng.IPromise<any> {
    const params: IUpdateSuspendedDataAlertSettingParams = {
      user,
      enabled
    };
    return this.ErrorNotifyingServer.sendRequest(new UpdateSuspendedDataAlertSettingRequest(params));
  }

  public getSuspendedDataAlertSetting(user: string): ng.IPromise<any> {
    const params: IGetSuspendedDataAlertSettingParams = {
      user
    };
    return this.ErrorNotifyingServer.sendRequest(new GetSuspendedDataAlertSettingRequest(params));
  }

  public updateMarkAnimationSetting(user: string, enabled: boolean): ng.IPromise<any> {
    const params: IUpdateMarkAnimationSettingParams = {
      user,
      enabled
    };
    return this.ErrorNotifyingServer.sendRequest(new UpdateMarkAnimationSettingRequest(params));
  }

  public getMarkAnimationSetting(user: string): ng.IPromise<any> {
    const params: IGetMarkAnimationSettingParams = {
      user
    };
    return this.ErrorNotifyingServer.sendRequest(new GetMarkAnimationSettingRequest(params));
  }
}

Module.instance().service('UserSettings', UserSettings);
